import { ReactElement, useContext } from 'react';

import { Circle } from '@calm-web/icons';

import FaveHeart from '@/components/app/contentPlayer/FaveHeart';
import { useMediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import { usePacksContext } from '@/components/app/layout/PacksProvider';
import { AuthModalContext } from '@/context/AuthModalContextProvider';
import { GuideState, useGuideInfo } from '@/hooks/app/useGuideInfo';
import { useGuidePlay } from '@/hooks/app/useGuidePlay';
import { useUserState } from '@/hooks/store';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { MeditationType } from '@/store/program/types';
import circleCheck from 'icons/circle-check.svg';

import { ClickableWrapper, Duration, GuidePosition, LockIcon, Title, Wrapper } from './styles';
import { GuideRowProps } from './types';

const GuideRow = ({ guide, program, programProgress }: GuideRowProps): ReactElement => {
	const user = useUserState();
	const routerPush = useRouterPush();
	const onGuidePlay = useGuidePlay();
	const { hideControls } = useMediaPlayerContext();
	const { setIsModalOpen } = useContext(AuthModalContext);
	const { currentGuide } = usePacksContext();
	const { guideState, title, isAvailable, isDisabled, duration } = useGuideInfo(
		guide,
		program,
		programProgress,
	);

	const isGuidePlaying = currentGuide?.id === guide.id;

	const onClick = async () => {
		if (!user) {
			setIsModalOpen(true);
			return;
		}

		if (program.meditation_type === MeditationType.Timer || isGuidePlaying) {
			return await routerPush(`/app/player/${guide.id}`);
		}

		await onGuidePlay(guide.id, program);
	};

	return (
		<Wrapper>
			<ClickableWrapper
				isDisabled={hideControls}
				isAvailable={isAvailable}
				onClick={onClick}
				data-testid="guide-row-clickable-wrapper"
			>
				<Title $isPlaying={isGuidePlaying}>
					{(guideState === GuideState.CourseCompleted || guideState === GuideState.CourseUnlocked) && (
						<GuidePosition as={guideState === GuideState.CourseCompleted ? circleCheck : Circle} />
					)}
					{title}
				</Title>
				{duration && <Duration>{duration}</Duration>}
			</ClickableWrapper>
			{!hideControls && isAvailable && <FaveHeart withBorder guideId={guide.id} guideTitle={guide.title} />}
			{!isAvailable && !isDisabled && <LockIcon data-testid="lock-icon" />}
		</Wrapper>
	);
};

export default GuideRow;
